import React, { useState, useRef, useEffect } from 'react';
import {
  TextField,
  Button,
  Stack,
  Grid,
  Typography,
  Chip,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  IconButton,
  Tooltip,
  Alert,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Switch,
  FormControlLabel,
  InputAdornment,
} from '@mui/material';
import {
  Mic as MicIcon,
  Stop as StopIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  ExpandMore as ExpandMoreIcon,
  Home as HomeIcon,
  Business as BusinessIcon,
  AttachMoney as MoneyIcon,
  LocationOn as LocationIcon,
  Build as BuildIcon,
  Photo as PhotoIcon,
  Tag as TagIcon,
} from '@mui/icons-material';

const statusOptions = [
  { value: 'Active', label: 'Active' },
  { value: 'Pending', label: 'Pending' },
  { value: 'Sold', label: 'Sold' },
  { value: 'OffMarket', label: 'Off Market' },
  { value: 'UnderContract', label: 'Under Contract' },
  { value: 'ComingSoon', label: 'Coming Soon' },
  { value: 'Expired', label: 'Expired' },
  { value: 'Withdrawn', label: 'Withdrawn' },
];

const typeOptions = [
  { value: 'Condo', label: 'Condo' },
  { value: 'House', label: 'House' },
  { value: 'Townhouse', label: 'Townhouse' },
  { value: 'Apartment', label: 'Apartment' },
  { value: 'Land', label: 'Land' },
  { value: 'Commercial', label: 'Commercial' },
  { value: 'MultiFamily', label: 'Multi-Family' },
  { value: 'Condo', label: 'Condo' },
  { value: 'Coop', label: 'Co-op' },
  { value: 'Mobile', label: 'Mobile Home' },
  { value: 'Farm', label: 'Farm' },
  { value: 'Ranch', label: 'Ranch' },
];

// MLS and Market Data fields
const mlsFields = [
  {
    name: 'mlsNumber', label: 'MLS Number', type: 'text', required: false,
  },
  {
    name: 'mlsSource', label: 'MLS Source', type: 'text', required: false,
  },
  {
    name: 'daysOnMarket', label: 'Days on Market', type: 'number', required: false,
  },
  {
    name: 'pricePerSqFt', label: 'Price per Sq Ft', type: 'number', required: false,
  },
  {
    name: 'lastSoldDate', label: 'Last Sold Date', type: 'date', required: false,
  },
  {
    name: 'lastSoldPrice', label: 'Last Sold Price', type: 'number', required: false,
  },
  {
    name: 'originalListPrice', label: 'Original List Price', type: 'number', required: false,
  },
  {
    name: 'listDate', label: 'List Date', type: 'date', required: false,
  },
  {
    name: 'expirationDate', label: 'Expiration Date', type: 'date', required: false,
  },
];

const marketDataFields = [
  {
    name: 'zestimate', label: 'Zestimate', type: 'number', required: false,
  },
  {
    name: 'rentZestimate', label: 'Rent Zestimate', type: 'number', required: false,
  },
  {
    name: 'walkScore', label: 'Walk Score', type: 'number', required: false,
  },
  {
    name: 'transitScore', label: 'Transit Score', type: 'number', required: false,
  },
  {
    name: 'bikeScore', label: 'Bike Score', type: 'number', required: false,
  },
  {
    name: 'schoolRating', label: 'School Rating', type: 'number', required: false,
  },
  {
    name: 'crimeRate', label: 'Crime Rate', type: 'text', required: false,
  },
  {
    name: 'neighborhood', label: 'Neighborhood', type: 'text', required: false,
  },
  {
    name: 'zipCode', label: 'ZIP Code', type: 'text', required: false,
  },
];

const VOICE_FIELD_MAP = {
  name: 'name',
  address: 'address',
  type: 'type',
  status: 'status',
  price: 'price',
  bedrooms: 'bedrooms',
  bathrooms: 'bathrooms',
  area: 'area',
  lotsize: 'lotSize',
  'lot size': 'lotSize',
  yearbuilt: 'yearBuilt',
  'year built': 'yearBuilt',
  description: 'description',
  tags: 'tags',
  notes: 'notes',
};

function PropertyForm({
  property = null,
  onSave,
  onCancel,
  loading = false,
}) {
  const [form, setForm] = useState({
    name: '',
    address: '',
    type: '',
    status: 'Active',
    price: '',
    agent: '',
    owner: '',
    watched: false,
    bedrooms: '',
    bathrooms: '',
    area: '',
    lotSize: '',
    yearBuilt: '',
    mls: '',
    description: '',
    tags: '',
    images: '',
    heating: '',
    cooling: '',
    appliances: '',
    utilities: '',
    hoa: '',
    taxes: '',
    insurance: '',
    // MLS Fields
    mlsNumber: '',
    mlsSource: '',
    daysOnMarket: '',
    pricePerSqFt: '',
    lastSoldDate: '',
    lastSoldPrice: '',
    originalListPrice: '',
    listDate: '',
    expirationDate: '',
    // Market Data Fields
    zestimate: '',
    rentZestimate: '',
    walkScore: '',
    transitScore: '',
    bikeScore: '',
    schoolRating: '',
    crimeRate: '',
    neighborhood: '',
    zipCode: '',
  });

  const [errors, setErrors] = useState({});
  const [isListening, setIsListening] = useState(false);
  const [voiceInput, setVoiceInput] = useState('');
  const [useNlp, setUseNlp] = useState(true);
  const [provider, setProvider] = useState('ollama');
  const recognitionRef = useRef(null);

  // Initialize form with property data
  useEffect(() => {
    if (property) {
      setForm({
        ...property,
        features: (property.features || []).join(', '),
        images: (property.images || []).join(', '),
        tags: (property.tags || []).join(', '),
      });
    } else {
      setForm({
        name: '',
        address: '',
        type: '',
        status: 'Active',
        price: '',
        agent: '',
        owner: '',
        watched: false,
        bedrooms: '',
        bathrooms: '',
        area: '',
        lotSize: '',
        yearBuilt: '',
        mls: '',
        description: '',
        features: '',
        images: '',
        tags: '',
        heating: '',
        cooling: '',
        appliances: '',
        utilities: '',
        hoa: '',
        taxes: '',
        insurance: '',
      });
    }
    setErrors({});
  }, [property]);

  const validateForm = () => {
    const newErrors = {};

    if (!form.address.trim()) {
      newErrors.address = 'Address is required';
    }

    if (!form.type.trim()) {
      newErrors.type = 'Property type is required';
    }

    if (form.price && (isNaN(form.price) || form.price < 0)) {
      newErrors.price = 'Price must be a positive number';
    }

    if (form.bedrooms && (isNaN(form.bedrooms) || form.bedrooms < 0)) {
      newErrors.bedrooms = 'Bedrooms must be a positive number';
    }

    if (form.bathrooms && (isNaN(form.bathrooms) || form.bathrooms < 0)) {
      newErrors.bathrooms = 'Bathrooms must be a positive number';
    }

    if (form.area && (isNaN(form.area) || form.area < 0)) {
      newErrors.area = 'Area must be a positive number';
    }

    if (form.yearBuilt && (isNaN(form.yearBuilt) || form.yearBuilt < 1800 || form.yearBuilt > new Date().getFullYear())) {
      newErrors.yearBuilt = 'Year built must be a valid year';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = () => {
    if (!validateForm()) {
      return;
    }

    const formData = {
      ...form,
      features: form.features ? form.features.split(',').map((feature) => feature.trim()).filter((feature) => feature) : [],
      images: form.images ? form.images.split(',').map((image) => image.trim()).filter((image) => image) : [],
      price: form.price ? parseFloat(form.price) : null,
      bedrooms: form.bedrooms ? parseInt(form.bedrooms) : null,
      bathrooms: form.bathrooms ? parseFloat(form.bathrooms) : null,
      area: form.area ? parseFloat(form.area) : null,
      lotSize: form.lotSize ? parseFloat(form.lotSize) : null,
      yearBuilt: form.yearBuilt ? parseInt(form.yearBuilt) : null,
      taxes: form.taxes ? parseFloat(form.taxes) : null,
      insurance: form.insurance ? parseFloat(form.insurance) : null,
      hoa: form.hoa ? parseFloat(form.hoa) : null,
      tags: form.tags ? form.tags.split(',').map((tag) => tag.trim()).filter((tag) => tag) : [],
    };

    onSave(formData);
  };

  const handleVoicePrompt = () => {
    if (!('webkitSpeechRecognition' in window)) {
      alert('Speech recognition not supported in this browser.');
      return;
    }

    if (isListening) {
      recognitionRef.current?.stop();
      setIsListening(false);
      return;
    }

    const recognition = new window.webkitSpeechRecognition();
    recognition.lang = 'en-US';
    recognition.interimResults = false;
    recognition.maxAlternatives = 1;

    recognition.onresult = async (event) => {
      const { transcript } = event.results[0][0];
      setVoiceInput(transcript);

      if (useNlp) {
        try {
          const res = await fetch('/api/v1/properties/extract-fields', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ transcript, provider }),
          });
          const data = await res.json();
          if (data && typeof data === 'object') {
            setForm((prev) => ({ ...prev, ...data }));
          }
        } catch (err) { /* ignore error */ }
      } else {
        const updates = {};
        const parts = transcript.toLowerCase().split(',').map((part) => part.trim());

        parts.forEach((part) => {
          Object.entries(VOICE_FIELD_MAP).forEach(([label, field]) => {
            if (part.startsWith(label)) {
              let value = part.replace(label, '').trim();
              if (field === 'tags') value = value.split(',').map((t) => t.trim()).filter(Boolean);
              updates[field] = value;
            }
          });
        });

        setForm((prev) => ({ ...prev, ...updates }));
      }
    };

    recognition.onerror = () => {
      setIsListening(false);
      setVoiceInput('');
    };

    recognition.onend = () => {
      setIsListening(false);
      setVoiceInput('');
    };

    recognition.start();
    recognitionRef.current = recognition;
    setIsListening(true);
  };

  const handleInputChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: '' }));
    }
  };

  return (
    <form onSubmit={(e) => { e.preventDefault(); handleSave(); }}>
      <Stack spacing={3} sx={{ mt: 1 }}>
        {/* Voice Input Section */}
        <Box>
          <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
            <Button
              variant={isListening ? 'outlined' : 'contained'}
              color={isListening ? 'error' : 'primary'}
              startIcon={isListening ? <StopIcon /> : <MicIcon />}
              onClick={handleVoicePrompt}
              size="small"
            >
              {isListening ? 'Stop Voice Input' : 'Voice Input'}
            </Button>
            <FormControlLabel
              control={<Switch checked={useNlp} onChange={(e) => setUseNlp(e.target.checked)} color="primary" />}
              label="NLP Voice Input"
              sx={{ ml: 2 }}
            />
            {useNlp && (
              <FormControl size="small" sx={{ minWidth: 160, ml: 2 }}>
                <InputLabel id="provider-label">Model</InputLabel>
                <Select
                  labelId="provider-label"
                  value={provider}
                  label="Model"
                  onChange={(e) => setProvider(e.target.value)}
                >
                  <MenuItem value="ollama">Ollama (Llama 3)</MenuItem>
                  <MenuItem value="llama4">Llama 4 (API)</MenuItem>
                  <MenuItem value="openai">OpenAI</MenuItem>
                </Select>
              </FormControl>
            )}
            {voiceInput && (
              <Typography variant="body2" color="text.secondary">
                Voice input: "
                {voiceInput}
                "
              </Typography>
            )}
          </Stack>
        </Box>

        {/* Basic Information */}
        <Accordion defaultExpanded>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Stack direction="row" spacing={1} alignItems="center">
              <HomeIcon color="primary" />
              <Typography variant="h6">Basic Information</Typography>
            </Stack>
          </AccordionSummary>
          <AccordionDetails>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  label="Name"
                  value={form.name}
                  onChange={(e) => handleInputChange('name', e.target.value)}
                  fullWidth
                  error={!!errors.name}
                  helperText={errors.name}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Address *"
                  value={form.address}
                  onChange={(e) => handleInputChange('address', e.target.value)}
                  fullWidth
                  error={!!errors.address}
                  helperText={errors.address}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth error={!!errors.type}>
                  <InputLabel>Property Type *</InputLabel>
                  <Select
                    value={form.type}
                    onChange={(e) => handleInputChange('type', e.target.value)}
                    label="Property Type *"
                  >
                    {typeOptions.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                  {errors.type && <FormHelperText>{errors.type}</FormHelperText>}
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={form.status}
                    onChange={(e) => handleInputChange('status', e.target.value)}
                    label="Status"
                  >
                    {statusOptions.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Price"
                  value={form.price}
                  onChange={(e) => handleInputChange('price', e.target.value)}
                  fullWidth
                  type="number"
                  InputProps={{
                    startAdornment: <InputAdornment position="start">$</InputAdornment>,
                  }}
                  error={!!errors.price}
                  helperText={errors.price}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="MLS Number"
                  value={form.mls}
                  onChange={(e) => handleInputChange('mls', e.target.value)}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  control={(
                    <Switch
                      checked={form.watched}
                      onChange={(e) => handleInputChange('watched', e.target.checked)}
                    />
                  )}
                  label="Watched Property"
                />
              </Grid>
            </Grid>
          </AccordionDetails>
        </Accordion>

        {/* Property Specifications */}
        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Stack direction="row" spacing={1} alignItems="center">
              <BuildIcon color="primary" />
              <Typography variant="h6">Property Specifications</Typography>
            </Stack>
          </AccordionSummary>
          <AccordionDetails>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Bedrooms"
                  value={form.bedrooms}
                  onChange={(e) => handleInputChange('bedrooms', e.target.value)}
                  fullWidth
                  type="number"
                  error={!!errors.bedrooms}
                  helperText={errors.bedrooms}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Bathrooms"
                  value={form.bathrooms}
                  onChange={(e) => handleInputChange('bathrooms', e.target.value)}
                  fullWidth
                  type="number"
                  error={!!errors.bathrooms}
                  helperText={errors.bathrooms}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Area"
                  value={form.area}
                  onChange={(e) => handleInputChange('area', e.target.value)}
                  fullWidth
                  type="number"
                  error={!!errors.area}
                  helperText={errors.area}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Lot Size"
                  value={form.lotSize}
                  onChange={(e) => handleInputChange('lotSize', e.target.value)}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Year Built"
                  value={form.yearBuilt}
                  onChange={(e) => handleInputChange('yearBuilt', e.target.value)}
                  fullWidth
                  type="number"
                  error={!!errors.yearBuilt}
                  helperText={errors.yearBuilt}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Features (comma separated)"
                  value={form.features}
                  onChange={(e) => handleInputChange('features', e.target.value)}
                  fullWidth
                  helperText="Enter features separated by commas"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Image URLs (comma separated)"
                  value={form.images}
                  onChange={(e) => handleInputChange('images', e.target.value)}
                  fullWidth
                  helperText="Enter image URLs separated by commas"
                />
              </Grid>
            </Grid>
          </AccordionDetails>
        </Accordion>

        {/* Contact Information */}
        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Stack direction="row" spacing={1} alignItems="center">
              <BusinessIcon color="primary" />
              <Typography variant="h6">Contact Information</Typography>
            </Stack>
          </AccordionSummary>
          <AccordionDetails>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Agent"
                  value={form.agent}
                  onChange={(e) => handleInputChange('agent', e.target.value)}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Owner"
                  value={form.owner}
                  onChange={(e) => handleInputChange('owner', e.target.value)}
                  fullWidth
                />
              </Grid>
            </Grid>
          </AccordionDetails>
        </Accordion>

        {/* Financial Information */}
        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Stack direction="row" spacing={1} alignItems="center">
              <MoneyIcon color="primary" />
              <Typography variant="h6">Financial Information</Typography>
            </Stack>
          </AccordionSummary>
          <AccordionDetails>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={4}>
                <TextField
                  label="HOA Fees"
                  value={form.hoa}
                  onChange={(e) => handleInputChange('hoa', e.target.value)}
                  fullWidth
                  type="number"
                  InputProps={{
                    startAdornment: <InputAdornment position="start">$</InputAdornment>,
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  label="Property Taxes"
                  value={form.taxes}
                  onChange={(e) => handleInputChange('taxes', e.target.value)}
                  fullWidth
                  type="number"
                  InputProps={{
                    startAdornment: <InputAdornment position="start">$</InputAdornment>,
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  label="Insurance"
                  value={form.insurance}
                  onChange={(e) => handleInputChange('insurance', e.target.value)}
                  fullWidth
                  type="number"
                  InputProps={{
                    startAdornment: <InputAdornment position="start">$</InputAdornment>,
                  }}
                />
              </Grid>
            </Grid>
          </AccordionDetails>
        </Accordion>

        {/* MLS Information */}
        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Stack direction="row" spacing={1} alignItems="center">
              <BusinessIcon color="primary" />
              <Typography variant="h6">MLS Information</Typography>
            </Stack>
          </AccordionSummary>
          <AccordionDetails>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="MLS Number"
                  value={form.mlsNumber}
                  onChange={(e) => handleInputChange('mlsNumber', e.target.value)}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="MLS Source"
                  value={form.mlsSource}
                  onChange={(e) => handleInputChange('mlsSource', e.target.value)}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Days on Market"
                  value={form.daysOnMarket}
                  onChange={(e) => handleInputChange('daysOnMarket', e.target.value)}
                  fullWidth
                  type="number"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Price per Sq Ft"
                  value={form.pricePerSqFt}
                  onChange={(e) => handleInputChange('pricePerSqFt', e.target.value)}
                  fullWidth
                  type="number"
                  InputProps={{
                    startAdornment: <InputAdornment position="start">$</InputAdornment>,
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Original List Price"
                  value={form.originalListPrice}
                  onChange={(e) => handleInputChange('originalListPrice', e.target.value)}
                  fullWidth
                  type="number"
                  InputProps={{
                    startAdornment: <InputAdornment position="start">$</InputAdornment>,
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Last Sold Price"
                  value={form.lastSoldPrice}
                  onChange={(e) => handleInputChange('lastSoldPrice', e.target.value)}
                  fullWidth
                  type="number"
                  InputProps={{
                    startAdornment: <InputAdornment position="start">$</InputAdornment>,
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  label="List Date"
                  value={form.listDate}
                  onChange={(e) => handleInputChange('listDate', e.target.value)}
                  fullWidth
                  type="date"
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  label="Last Sold Date"
                  value={form.lastSoldDate}
                  onChange={(e) => handleInputChange('lastSoldDate', e.target.value)}
                  fullWidth
                  type="date"
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  label="Expiration Date"
                  value={form.expirationDate}
                  onChange={(e) => handleInputChange('expirationDate', e.target.value)}
                  fullWidth
                  type="date"
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
            </Grid>
          </AccordionDetails>
        </Accordion>

        {/* Market Data */}
        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Stack direction="row" spacing={1} alignItems="center">
              <LocationIcon color="primary" />
              <Typography variant="h6">Market Data & Neighborhood</Typography>
            </Stack>
          </AccordionSummary>
          <AccordionDetails>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Zestimate"
                  value={form.zestimate}
                  onChange={(e) => handleInputChange('zestimate', e.target.value)}
                  fullWidth
                  type="number"
                  InputProps={{
                    startAdornment: <InputAdornment position="start">$</InputAdornment>,
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Rent Zestimate"
                  value={form.rentZestimate}
                  onChange={(e) => handleInputChange('rentZestimate', e.target.value)}
                  fullWidth
                  type="number"
                  InputProps={{
                    startAdornment: <InputAdornment position="start">$</InputAdornment>,
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  label="Walk Score"
                  value={form.walkScore}
                  onChange={(e) => handleInputChange('walkScore', e.target.value)}
                  fullWidth
                  type="number"
                  inputProps={{ min: 0, max: 100 }}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  label="Transit Score"
                  value={form.transitScore}
                  onChange={(e) => handleInputChange('transitScore', e.target.value)}
                  fullWidth
                  type="number"
                  inputProps={{ min: 0, max: 100 }}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  label="Bike Score"
                  value={form.bikeScore}
                  onChange={(e) => handleInputChange('bikeScore', e.target.value)}
                  fullWidth
                  type="number"
                  inputProps={{ min: 0, max: 100 }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="School Rating"
                  value={form.schoolRating}
                  onChange={(e) => handleInputChange('schoolRating', e.target.value)}
                  fullWidth
                  type="number"
                  inputProps={{ min: 0, max: 10 }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Crime Rate"
                  value={form.crimeRate}
                  onChange={(e) => handleInputChange('crimeRate', e.target.value)}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Neighborhood"
                  value={form.neighborhood}
                  onChange={(e) => handleInputChange('neighborhood', e.target.value)}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="ZIP Code"
                  value={form.zipCode}
                  onChange={(e) => handleInputChange('zipCode', e.target.value)}
                  fullWidth
                />
              </Grid>
            </Grid>
          </AccordionDetails>
        </Accordion>

        {/* Additional Information */}
        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Stack direction="row" spacing={1} alignItems="center">
              <TagIcon color="primary" />
              <Typography variant="h6">Additional Information</Typography>
            </Stack>
          </AccordionSummary>
          <AccordionDetails>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  label="Description"
                  value={form.description}
                  onChange={(e) => handleInputChange('description', e.target.value)}
                  fullWidth
                  multiline
                  rows={4}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Tags (comma separated)"
                  value={form.tags}
                  onChange={(e) => handleInputChange('tags', e.target.value)}
                  fullWidth
                  helperText="Enter tags separated by commas"
                />
              </Grid>
            </Grid>
          </AccordionDetails>
        </Accordion>
      </Stack>
      <Box sx={{
        display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 3,
      }}
      >
        <Button onClick={onCancel} startIcon={<CancelIcon />}>
          Cancel
        </Button>
        <Button
          type="submit"
          variant="contained"
          startIcon={<SaveIcon />}
          disabled={loading}
        >
          {loading ? 'Saving...' : (property ? 'Update' : 'Save')}
        </Button>
      </Box>
    </form>
  );
}

export default PropertyForm;
